import { useState } from 'react';
import { mediaDeLigne } from '../api/mappers';
import { piecesApi } from '../api/resources';

import { COTE_MAX, reduitLImage } from '../lib/image';
import { TAILLE_MAX_PIECE, enMo, estEnregistre } from '../lib/medias';
import { C } from '../theme/palette';

/**
 * Photos et plans d'une ligne de devis.
 *
 * Le plan de la cuisine, la photo du mur à reprendre ou celle du carrelage
 * proposé expliquent une ligne, pas le devis entier. Les fichiers partent au
 * stockage du serveur un par un ; la ligne n'en garde que l'adresse, reçue
 * avec le devis.
 *
 * @param {object} props propriétés du composant
 * @param {number|string} props.devisId devis concerné
 * @param {string} [props.cle] clé stable de la ligne, attribuée par le serveur
 * @param {object[]} [props.medias] photos et plans déjà joints
 * @param {(medias: object[]) => void} props.onChange liste à jour, après un dépôt ou un retrait
 * @param {'DEVIS'|'FACTURE'} [props.genre] la ligne est d'une offre ou d'une facture
 * @returns {import('react').ReactElement} la galerie et ses commandes
 */
export function MediasLigne({ devisId, cle, medias, onChange, genre = 'DEVIS' }) {
  const liste = medias || [];
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [grand, setGrand] = useState(null);

  if (liste.length === 0 && !ouvert) {
    return (
      <button
        type="button"
        onClick={() => setOuvert(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 800,
          color: C.teal,
          cursor: 'pointer',
          background: C.tealL,
          border: '1px solid ' + C.teal + '44',
          borderRadius: 9,
          padding: '9px 13px',
        }}
      >
        📎 Ajouter une photo ou un plan{' '}
        <span style={{ fontWeight: 600, fontSize: 10, opacity: 0.75 }}>facultatif</span>
      </button>
    );
  }

  if (!estEnregistre(devisId) || !cle) {
    return (
      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
        {genre === 'FACTURE'
          ? 'Enregistre la facture pour joindre des photos et des plans à cette ligne.'
          : 'Enregistre l’offre pour joindre des photos et des plans à cette ligne.'}
      </div>
    );
  }

  const depose = async (evenement, typePiece) => {
    const fichier = evenement.target.files && evenement.target.files[0];
    // Vidé aussitôt : choisir deux fois le même fichier doit déclencher l'envoi.
    evenement.target.value = '';
    if (!fichier) return;
    setErreur('');
    setEnvoi(true);
    try {
      const reduit = await reduitLImage(fichier, COTE_MAX);
      if (reduit.size > TAILLE_MAX_PIECE) {
        setErreur(
          `Ce fichier pèse ${enMo(reduit.size)} Mo, au-delà des ${enMo(TAILLE_MAX_PIECE)} Mo admis.`,
        );
        return;
      }
      const piece = await piecesApi.deposeSurLigne(
        devisId,
        cle,
        reduit,
        typePiece,
        fichier.name,
        genre,
      );
      onChange([...liste, mediaDeLigne(piece)]);
    } catch (e) {
      setErreur(e?.message || 'L’envoi a échoué.');
    } finally {
      setEnvoi(false);
    }
  };

  const retire = async (media) => {
    const quoi = media.type === 'Plan' ? 'ce plan' : 'cette photo';
    if (!window.confirm(`Retirer ${quoi} ? Le fichier sera effacé.`)) return;
    setErreur('');
    try {
      await piecesApi.retire(media.id);
      onChange(liste.filter((m) => m.id !== media.id));
    } catch (e) {
      setErreur(e?.message || 'Le retrait a échoué.');
    }
  };

  const case64 = {
    width: 64,
    height: 64,
    borderRadius: 11,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: envoi ? 'wait' : 'pointer',
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
        {liste.map((m) => (
          <div key={m.id} style={{ width: 64 }}>
            <div
              onClick={() => setGrand(m)}
              style={{
                ...case64,
                cursor: 'pointer',
                overflow: 'hidden',
                border: '1px solid ' + C.border,
                background: '#fff',
                position: 'relative',
              }}
            >
              {m.pdf ? (
                <div style={{ color: C.red, fontSize: 20, textAlign: 'center', lineHeight: 1.1 }}>
                  📄<div style={{ fontSize: 8, fontWeight: 800 }}>PDF</div>
                </div>
              ) : (
                <img
                  src={m.data}
                  alt={m.nom || 'Photo de la ligne'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              )}
              <button
                type="button"
                aria-label={m.type === 'Plan' ? 'Retirer le plan' : 'Retirer la photo'}
                onClick={(e) => {
                  e.stopPropagation();
                  retire(m);
                }}
                style={{
                  position: 'absolute',
                  top: 3,
                  right: 3,
                  width: 18,
                  height: 18,
                  borderRadius: 99,
                  border: 'none',
                  background: 'rgba(15,22,38,.6)',
                  color: '#fff',
                  fontSize: 10,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                fontSize: 9,
                color: C.grey,
                fontWeight: 700,
                textAlign: 'center',
                marginTop: 4,
              }}
            >
              {m.type === 'Plan' ? '✏️ Plan' : '📷 Photo'}
            </div>
          </div>
        ))}
        <label style={{ width: 64 }}>
          <div
            style={{
              ...case64,
              border: '1.5px dashed ' + C.teal,
              background: C.tealL,
              color: C.teal,
              opacity: envoi ? 0.6 : 1,
            }}
          >
            <div style={{ fontSize: 20, lineHeight: 1 }}>＋</div>
            <div style={{ fontSize: 9, fontWeight: 800 }}>Photo</div>
          </div>
          <input
            type="file"
            accept="image/*"
            aria-label="Ajouter une photo à la ligne"
            disabled={envoi}
            style={{ display: 'none' }}
            onChange={(e) => depose(e, 'PHOTO')}
          />
        </label>
        <label style={{ width: 64 }}>
          <div
            style={{
              ...case64,
              border: '1.5px dashed ' + C.purple,
              background: '#F5EEFE',
              color: C.purple,
              opacity: envoi ? 0.6 : 1,
            }}
          >
            <div style={{ fontSize: 20, lineHeight: 1 }}>＋</div>
            <div style={{ fontSize: 9, fontWeight: 800 }}>Plan</div>
          </div>
          <input
            type="file"
            accept="image/*,application/pdf"
            aria-label="Ajouter un plan à la ligne"
            disabled={envoi}
            style={{ display: 'none' }}
            onChange={(e) => depose(e, 'PLAN')}
          />
        </label>
      </div>
      <div style={{ fontSize: 10, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
        💻 Ordinateur : choisis un fichier (photo, plan PDF). 📱 Mobile : appareil photo ou galerie.
        Touche une vignette pour l’agrandir.
      </div>
      {envoi && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Envoi en cours…</div>}
      {erreur && (
        <div role="alert" style={{ marginTop: 6, fontSize: 11, color: C.red, lineHeight: 1.5 }}>
          {erreur}
        </div>
      )}
      {grand && (
        <div
          onClick={() => setGrand(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,.93)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            paddingTop: 'max(16px, var(--st-haut))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          }}
        >
          <button
            type="button"
            onClick={() => setGrand(null)}
            style={{
              alignSelf: 'flex-end',
              background: 'rgba(255,255,255,.18)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 13px',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
              marginBottom: 10,
            }}
          >
            ✕ Fermer
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', maxWidth: '100%' }}
          >
            {grand.pdf ? (
              <div style={{ padding: '38px 34px', textAlign: 'center' }}>
                <div style={{ fontSize: 44 }}>📄</div>
                <div style={{ fontSize: 12, fontWeight: 800, marginTop: 8 }}>
                  {grand.nom || 'Plan.pdf'}
                </div>
                <a
                  href={grand.data}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: 13,
                    background: C.teal,
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: 8,
                    padding: '9px 15px',
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  ⬇ Ouvrir le PDF
                </a>
              </div>
            ) : (
              <img
                src={grand.data}
                alt=""
                style={{ display: 'block', maxWidth: '100%', maxHeight: '78vh' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
